import type { ReactNode } from "react";
import dayjs from "dayjs";
import { DatePicker, Input, Select, Switch } from "antd";

import type { Frequency } from "../../types";
import SubmitButton from "../SubmitButton";

const LABEL_START_DATE = "Start Date";
const LABEL_FREQUENCY = "Frequency";
const LABEL_ROUND_CENTS = "Round Cents";

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: "MONTHLY",       label: "Monthly" },
  { value: "QUARTERLY",     label: "Quarterly" },
  { value: "SEMI_ANNUALLY", label: "Semi-Annually" },
  { value: "ANNUALLY",      label: "Annually" },
];

type FormField = {
  key: string;
  itemProps: Record<string, unknown>;
  children: ReactNode;
};

type FormFieldsOptions = {
  isUpdate: boolean;
  isLoading: boolean;
  isDeleting: boolean;
  onDelete: () => void;
};

const getFormFields = ({
  isUpdate,
  isLoading,
  isDeleting,
  onDelete,
}: FormFieldsOptions): FormField[] => [
  {
    key: "projectId",
    itemProps: { name: "projectId", hidden: true },
    children: <Input />,
  },
  {
    key: "name",
    itemProps: { name: "name", hidden: true },
    children: <Input />,
  },
  {
    key: "transactionType",
    itemProps: { name: "transactionType", hidden: true },
    children: <Input />,
  },
  {
    key: "nextDate",
    itemProps: { name: "nextDate", hidden: true },
    children: <Input />,
  },
  {
    key: "frequency",
    itemProps: {
      name: "distributionFrequency",
      label: LABEL_FREQUENCY,
      rules: [{ required: true, message: `${LABEL_FREQUENCY} is required` }],
    },
    children: (
      <Select
        style={{ width: 140 }}
        options={FREQUENCY_OPTIONS}
        placeholder={LABEL_FREQUENCY}
      />
    ),
  },
  {
    key: "startDate",
    itemProps: {
      name: "startDate",
      label: LABEL_START_DATE,
      rules: [{ required: true, message: `${LABEL_START_DATE} is required` }],
      normalize: (value: dayjs.Dayjs | null) => value ? value.toISOString() : value,
      getValueProps: (value: string | null) => ({ value: value ? dayjs(value) : value }),
    },
    children: (
      <DatePicker
        picker="date"
        format="MM/DD/YYYY"
        placeholder={LABEL_START_DATE}
        allowClear={false}
      />
    ),
  },
  {
    key: "roundCents",
    itemProps: {
      name: "shouldRoundCents",
      label: LABEL_ROUND_CENTS,
      valuePropName: "checked",
    },
    children: <Switch />,
  },
  {
    key: "submit",
    itemProps: { style: { margin: 0 } },
    children: (
      <SubmitButton
        isUpdate={isUpdate}
        isLoading={isLoading}
        isDeleting={isDeleting}
        onDelete={onDelete}
      />
    ),
  },
];

export default getFormFields;
